import { useState } from 'react'
import { Table, Button, Modal, Form, Input, message, Tag } from 'antd'
import { PlusOutlined, EditOutlined, DeleteOutlined, ExclamationCircleOutlined } from '@ant-design/icons'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { categoryApi, Category } from '../services/categories'

const ResourceCategory = () => {
  const [isModalVisible, setIsModalVisible] = useState(false)
  const [editingCategory, setEditingCategory] = useState<Category | null>(null)
  const [form] = Form.useForm()
  const queryClient = useQueryClient()

  // Fetch categories using React Query
  const { data: categoriesData, isLoading, error } = useQuery({
    queryKey: ['categories'],
    queryFn: categoryApi.getCategories,
  })

  // Create category mutation
  const createMutation = useMutation({
    mutationFn: categoryApi.createCategory,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['categories'] })
      message.success('Category created successfully!')
      setIsModalVisible(false)
      form.resetFields()
    },
    onError: (error: any) => {
      message.error(error.response?.data?.message || 'Failed to create category')
    },
  })

  // Update category mutation
  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: { name: string } }) =>
      categoryApi.updateCategory(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['categories'] })
      message.success('Category updated successfully!')
      setIsModalVisible(false)
      form.resetFields()
    },
    onError: (error: any) => {
      message.error(error.response?.data?.message || 'Failed to update category')
    },
  })

  // Delete category mutation
  const deleteMutation = useMutation({
    mutationFn: categoryApi.deleteCategory,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['categories'] })
      message.success('Category deleted successfully!')
    },
    onError: (error: any) => {
      message.error(error.response?.data?.message || 'Failed to delete category')
    },
  })

  const categories = categoriesData?.data || []

  const handleAddCategory = () => {
    setEditingCategory(null)
    form.resetFields()
    setIsModalVisible(true)
  }

  const handleEditCategory = (record: Category) => {
    setEditingCategory(record)
    form.setFieldsValue({
      name: record.name
    })
    setIsModalVisible(true)
  }

  const handleModalOk = () => {
    form.validateFields().then((values) => {
      const categoryData = {
        name: values.name
      }

      if (editingCategory) {
        // Update existing category
        updateMutation.mutate({ id: editingCategory._id, data: categoryData })
      } else {
        // Create new category
        createMutation.mutate(categoryData)
      }
    })
  }

  const handleModalCancel = () => {
    setIsModalVisible(false)
    form.resetFields()
  }

  const handleDeleteCategory = (record: Category) => {
    Modal.confirm({
      icon: null,
      title: (
        <div style={{ textAlign: 'center' }}>
          <ExclamationCircleOutlined style={{ fontSize: 48, color: '#faad14', marginBottom: 16 }} />
          <div>Are you sure you want to delete this category?</div>
        </div>
      ),
      content: (
        <div style={{ textAlign: 'center' }}>
          This action cannot be undone for "<b>{record.name}</b>".
        </div>
      ),
      footer: (
        <div style={{ display: 'flex', gap: '12px', justifyContent: 'center' }} className='mt-4'>
          <Button
            onClick={() => Modal.destroyAll()}
            style={{ width: '100%', height: '40px' }}
            className='!rounded-[10px] !h-9 !text-xs'
          >
            Cancel
          </Button>
          <Button
            type="primary"
            danger
            onClick={() => {
              deleteMutation.mutate(record._id)
              Modal.destroyAll()
            }}
            loading={deleteMutation.isPending}
            style={{ width: '100%', height: '40px' }}
            className='!rounded-[10px] !h-9 !text-xs'
          >
            Yes, Delete
          </Button>
        </div>
      ),
      centered: true,
    })
  }

  const columns = [
    {
      title: 'Category',
      dataIndex: 'name',
      key: 'name',
      render: (text: string) => (
        <span className="font-medium text-gray-900">{text}</span>
      ),
    },
    {
      title: 'Action',
      key: 'action',
      render: (record: Category) => (
        <div className="flex items-center gap-3">
          <Button
            type="text"
            icon={<EditOutlined />}
            onClick={(e) => {
              e.preventDefault()
              e.stopPropagation()
              handleEditCategory(record)
            }}
            className="text-gray-500 hover:text-primary p-1"
          />
          <Button
            type="text"
            icon={<DeleteOutlined />}
            onClick={(e) => {
              e.preventDefault()
              e.stopPropagation()
              handleDeleteCategory(record)
            }}
            className="text-gray-500 hover:text-red-500 p-1"
          />
        </div>
      ),
    },
  ]

  return (
    <div className="p-6 h-full flex flex-col">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center justify-between">
          <h1 className="text-2xl font-semibold text-gray-900">Resource category</h1>
          <Button 
            type="primary" 
            onClick={handleAddCategory}
            className="bg-primary hover:bg-primary/90 h-9 !text-sm !rounded-[10px]"
          >
            Add category
          </Button>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 flex-1">
        {isLoading ? (
          <div className="p-6">
            <div className="animate-pulse">
              {/* Table Header Skeleton */}
              <div className="flex items-center justify-between mb-4">
                <div className="h-4 bg-gray-200 rounded w-32"></div>
                <div className="h-4 bg-gray-200 rounded w-24"></div>
              </div>
              
              {/* Table Rows Skeleton */}
              {[...Array(5)].map((_, index) => (
                <div key={index} className="flex items-center justify-between py-4 border-b border-gray-100">
                  <div className="h-4 bg-gray-200 rounded w-48"></div>
                  <div className="flex gap-2">
                    <div className="h-8 bg-gray-200 rounded w-8"></div>
                    <div className="h-8 bg-gray-200 rounded w-8"></div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ) : error ? (
          <div className="flex justify-center items-center h-64">
            <div className="text-center">
              <p className="text-red-600 mb-4">Failed to load categories</p>
              <Button onClick={() => window.location.reload()}>Retry</Button>
            </div>
          </div>
        ) : (
          <Table
            columns={columns}
            dataSource={categories.map(category => ({ ...category, key: category._id }))}
            pagination={false}
            className="category-table"
            rowClassName="hover:bg-gray-50"
          />
        )}
      </div>

      {/* Edit/Add Modal */}
      <Modal
        title={editingCategory ? "Edit Category" : "Add Category"}
        open={isModalVisible}
        onOk={handleModalOk}
        onCancel={handleModalCancel}
        okText="Save Changes"
        cancelText="Cancel"
        className="category-modal"
        centered={true}
        okButtonProps={{
          className: "bg-primary hover:bg-primary/90",
          loading: createMutation.isPending || updateMutation.isPending
        }}
      >
        <Form
          form={form}
          layout="vertical"
          className="mt-6"
        >
          <Form.Item
            label={<span className="text-xs font-medium text-gray-700">Category Name*</span>}
            name="name"
            rules={[{ required: true, message: 'Please enter category name' }]}
            required={false}
          >
            <Input 
              placeholder="Enter category name"
              className="h-10 rounded-lg border-gray-300 focus:border-primary focus:ring-primary"
            />
          </Form.Item>

        </Form>
      </Modal>
    </div>
  )
}

export default ResourceCategory
